import React from "react";
import { Link } from "react-router-dom";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { ArrowRight, BadgeCheck } from "lucide-react";

const Foto = ({ n, alt, className = "", testid }) => (
  <img
    src={`/images/studio/studio-${n}.webp`}
    alt={alt}
    width="640"
    height="480"
    loading="lazy"
    decoding="async"
    draggable="false"
    className={`w-full aspect-[4/3] object-cover rounded border border-[#E8E2D5] pointer-events-none select-none ${className}`}
    data-testid={testid}
  />
);

/* Testi approvati dalla committente (09/10/2026): non riscrivere o accorciare.
   Credenziali e dati societari ripresi dalla versione precedente della pagina (verificabili).
   Nessun nome, qualifica o numero di professionisti inventato. */

const P = ({ children }) => <p className="text-[#3B4658] leading-relaxed mb-4">{children}</p>;

const CREDENZIALI = [
  { label: "Dottore Commercialista", value: "Albo ODCEC di Ravenna n. 511 A · dal 23/01/2007" },
  { label: "Revisore Legale", value: "n. 1456060 · G.U. 62 del 07/08/2007" },
  { label: "SC Dottori Commercialisti", value: "S.r.l. STP · n. 11 STP dell'Albo ODCEC di Ravenna" }
];

const COMPETENZE = [
  ["Contabilità e bilancio", "Contabilità aggiornata, bilanci e adempimenti periodici."],
  ["Fiscalità e pianificazione", "Dichiarazioni, adempimenti e pianificazione fiscale durante l'anno."],
  ["Gestione del personale", "Adempimenti e aspetti amministrativi legati ai dipendenti."],
  ["Controllo di gestione", "Analisi di fatturato, margini, costi, flussi e indicatori."],
  ["Questioni societarie", "Operazioni e adempimenti della vita societaria."],
  ["Ambiti specialistici", "Affrontati con professionisti dedicati quando l'incarico lo richiede."]
];

const ChiSiamoPage = () => (
  <main className="pt-20" data-testid="chi-siamo-page">
    <PageSEO {...SEO.chiSiamo} />

    <section className="py-16 md:py-24 bg-[#FAF8F5]">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-start">
        <div>
          <p className="text-gold font-bold tracking-widest text-sm mb-5">CHI SIAMO</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] leading-tight mb-5">
            SILVIA CASALI.<br /><span className="text-gold">UNA VISIONE, UN TEAM DI PROFESSIONISTI.</span>
          </h1>
          <p className="font-heading text-xl md:text-2xl text-[#0D1527] leading-snug mb-8">
            Il commercialista come partner strategico. E uno studio che lavora al tuo fianco.
          </p>
          <P>Ho fondato il mio studio con la convinzione che i numeri possano aiutare gli imprenditori a prendere decisioni migliori.</P>
          <P>Perché dietro ogni bilancio c'è un'impresa. E dietro ogni impresa ci sono persone che ogni giorno prendono decisioni, affrontano difficoltà, investono, rischiano e cercano di costruire qualcosa.</P>
          <P>È da questa convinzione che è nato il mio modo di lavorare.</P>
          <P>Mettere i numeri al servizio delle persone e delle loro decisioni.</P>
          <P>Ma per offrire un'assistenza davvero completa non basta la competenza di una sola persona.</P>
          <P>Per questo ho costruito SC Dottori Commercialisti S.r.l. STP, uno studio in cui professionisti con competenze diverse lavorano in modo coordinato per affiancare imprenditori e aziende nella gestione contabile, fiscale, amministrativa e strategica.</P>
          <P>Io sono il punto di riferimento e coordino l'approccio professionale dello studio.</P>
          <P>Accanto a me ci sono persone che, con ruoli e specializzazioni differenti, contribuiscono ogni giorno alla qualità del lavoro che offriamo.</P>
          <p className="font-heading text-2xl md:text-3xl text-[#0D1527] leading-snug border-l-4 border-gold pl-6 mt-8">
            Perché un imprenditore deve poter contare su un professionista di fiducia. Ma anche sulla solidità di un'intera squadra.
          </p>
        </div>
        <figure className="lg:sticky lg:top-28">
          <img
            src="/chi-siamo/silvia-casali-studio.webp"
            alt="Silvia Casali, Dottore Commercialista e Revisore Legale, fondatrice di SC Dottori Commercialisti"
            width="900"
            height="1352"
            className="w-full rounded-lg border border-[#E8E2D5] shadow-[0_24px_60px_-30px_rgba(13,21,39,0.45)]"
            data-testid="chi-siamo-foto-silvia"
          />
          <figcaption className="text-[#4A5568] text-sm mt-4">
            <span className="font-bold text-[#0D1527]">Silvia Casali</span> — Dottore Commercialista e Revisore Legale,
            fondatrice di SC Dottori Commercialisti S.r.l. STP
          </figcaption>
        </figure>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-white border-y border-[#E8E2D5]" data-testid="chi-siamo-studio">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <p className="text-gold font-bold tracking-[0.28em] text-xs mb-4">LO STUDIO</p>
        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] leading-tight mb-5 max-w-3xl">
          Uno studio organizzato, dove ogni attività ha un responsabile e una scadenza presidiata.
        </h2>
        <p className="text-[#3B4658] leading-relaxed max-w-2xl mb-8">
          Spazi di lavoro condivisi, procedure comuni e coordinamento quotidiano: la tua azienda non dipende da una
          sola persona e le scadenze non restano mai scoperte.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl" data-testid="chi-siamo-foto-studio">
          <Foto n="04" alt="Gli spazi di lavoro dello studio SC Dottori Commercialisti" testid="chi-siamo-foto-1" />
          <Foto n="05" alt="Una riunione di lavoro nello studio SC Dottori Commercialisti" testid="chi-siamo-foto-2" />
          <Foto n="03" alt="Il corridoio dello studio SC Dottori Commercialisti a Ravenna" testid="chi-siamo-foto-3" />
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="chi-siamo-team">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] leading-tight mb-3">
          Un team, competenze diverse, un unico obiettivo.
        </h2>
        <p className="font-heading text-xl text-gold mb-8">Aiutarti a gestire meglio la tua azienda.</p>
        <div className="max-w-3xl">
          <P>Dalla contabilità alla pianificazione fiscale, dalla gestione del personale al controllo di gestione, fino alle questioni societarie e agli ambiti specialistici affrontati con professionisti dedicati.</P>
          <P>Ogni attività richiede competenze specifiche.</P>
          <P>Il nostro lavoro è coordinarle, mantenendo una visione d'insieme della tua impresa.</P>
          <P>Non vogliamo che tu debba raccontare ogni volta da capo la tua situazione o cercare da solo di collegare informazioni contabili, fiscali e gestionali.</P>
          <P>Vogliamo che tu abbia uno studio che conosce la tua azienda e lavora con una direzione condivisa.</P>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {COMPETENZE.map(([t, d]) => (
            <div key={t} className="bg-white border border-[#E8E2D5] rounded-lg p-5">
              <p className="font-bold text-[#0D1527] mb-1">{t}</p>
              <p className="text-[#4A5568] text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-[#FAF8F5]" data-testid="chi-siamo-differenza">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1fr_260px] gap-10 items-start">
        <div>
          <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] mb-6">La differenza è nel modo in cui lavoriamo.</h2>
          <P>Per noi una contabilità aggiornata non è un semplice archivio di registrazioni.</P>
          <P>È il punto di partenza per capire come sta andando un'impresa.</P>
          <P>Per questo affianchiamo alla gestione contabile analisi periodiche e pianificazione fiscale.</P>
          <P>E per chi desidera approfondire, sviluppiamo percorsi di controllo di gestione, analisi dei margini, dei flussi finanziari e cruscotti direzionali.</P>
          <P>Non crediamo che ogni imprenditore debba diventare un esperto di bilanci.</P>
          <P>Crediamo che ogni imprenditore abbia il diritto di comprendere i numeri della propria azienda.</P>
          <P>E di avere accanto professionisti capaci di spiegarglieli.</P>
        </div>
        <div className="space-y-4 max-w-[260px]">
          <Foto n="07" alt="Un incontro di consulenza con gli imprenditori nello studio" testid="chi-siamo-foto-4" />
          <Foto n="08" alt="La sala di attesa dello studio SC Dottori Commercialisti" testid="chi-siamo-foto-5" />
        </div>
      </div>
    </section>

    <section className="py-16 md:py-20 bg-[#F4EFEA]" data-testid="chi-siamo-silvia">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0D1527] mb-2">Silvia Casali</h2>
        <p className="text-gold font-bold mb-7">Dottore Commercialista e Revisore Legale.</p>
        <P>Ho fondato questo studio con un'idea precisa: costruire un rapporto diverso tra commercialista e imprenditore.</P>
        <P>Un rapporto fatto di competenza, chiarezza, continuità e confronto.</P>
        <P>
          Sono anche autrice di <Link to="/libro" className="text-gold font-semibold hover:underline">Dai numeri alle decisioni</Link>,
          un libro nato dalla stessa convinzione che guida il mio lavoro: i numeri acquistano valore quando aiutano a fare scelte migliori.
        </P>
        <p className="font-heading text-2xl md:text-3xl text-[#0D1527] leading-snug border-l-4 border-gold pl-6 my-8">
          Il mio nome rappresenta il progetto. Il nostro team lo rende concreto ogni giorno.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mt-8" data-testid="chi-siamo-credenziali">
          {CREDENZIALI.map((c) => (
            <div key={c.label} className="bg-white border border-[#E8E2D5] rounded-lg p-5">
              <BadgeCheck className="text-gold mb-2" size={20} />
              <p className="font-bold text-[#0D1527] text-sm mb-1">{c.label}</p>
              <p className="text-[#4A5568] text-xs leading-relaxed">{c.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 md:py-24 bg-[#0D1527]" data-testid="chi-siamo-cta">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-6">
          Cerchi un commercialista che conosca davvero la tua azienda?
        </h2>
        <p className="font-heading text-2xl text-gold mb-6">Conosciamoci.</p>
        <p className="text-white/75 leading-relaxed mb-10">
          Raccontaci la tua attività e le esigenze che vorresti affrontare con maggiore consapevolezza.
        </p>
        <Link
          to="/contatti"
          className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105"
          data-testid="chi-siamo-cta-contatti"
        >
          CONTATTA IL NOSTRO STUDIO <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  </main>
);

export { ChiSiamoPage };
